import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import { PortfolioPerformance } from 'src/app/domain/pp/dto/PortfolioPerformance';
import {
  CHART_COLORS, CHART_FONTS,
  CHART_STYLES,
  getChartSecondaryAxis,
  getChartSeriesObj,
  HighchartsOptions,
  parseDate,
} from "src/app/domain/shared/component/pie-chart-simple/highcharts-common/highcharts-global-const";

@Component({
  selector: 'app-company-card-chart',
  templateUrl: './company-card-chart.component.html',
  styleUrls: ['./company-card-chart.component.scss'],
})
export class CompanyCardChartComponent implements OnChanges {
  @Input() pps: PortfolioPerformance[] = [];
  @Input() title: CardChartTitle;

  /** {@variable options} represents HighCharts fine tuning.
   * See: {@link https://www.highcharts.com/docs/getting-started/how-to-set-options}
   */
  options: HighchartsOptions;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.cardChartProps || changes.pps) {
      this.setOptions();
      if (this.pps.length) {
        this.getChartData();
        Highcharts.chart('companyCardChart', this.options);
      }
    }
  }

  private getChartData(): void {
    let periods: Date[] = [...new Set(this.pps.map((p) => p.period).sort())];
    if (periods.length > 20) {
      periods = periods.slice(periods.length - 20); // take only last 20 elements
    }

    periods.forEach((period) => {
      this.fillChartData(period);
    });
  }

  /* eslint-disable */
  private fillChartData(period: Date): void {
    const ppsByPeriod: PortfolioPerformance[] = this.pps.filter((pp) => pp.period === period);
    const cost: number = ppsByPeriod.reduce((previousValue, currentValue) => (previousValue + currentValue.cost), 0);
    const fairValue: number = ppsByPeriod.reduce((previousValue, currentValue) => (previousValue + currentValue.fairValue), 0);
    const gainLoss: number = ppsByPeriod.reduce((previousValue, currentValue) =>
      (previousValue + (currentValue.fairValue - currentValue.cost)), 0);
    const holding: number = ppsByPeriod.reduce((previousValue, currentValue) => (previousValue + currentValue.holdingPercentage), 0);
    const moic: number = cost ? fairValue / cost : 0;
    this.options.series[0].data.push(gainLoss / Math.pow(10, 6));
    this.options.series[1].data.push(cost / Math.pow(10, 6));
    this.options.series[2].data.push(100 * holding);
    this.options.series[3].data.push(moic);
    this.options.xAxis[0].categories.push(parseDate(period));
  }
  /* eslint-enable */

  private setOptions(): void {
    const isMoicCardChart = this.title === CardChartTitle.moic;
    this.options = {
      credits: { enabled: false },
      chart: {
        zoomType: 'xy',
        reflow: true,
        zooming: {
          mouseWheel: false,
        },
      },
      title: {
        text: this.title,
        style: CHART_STYLES.title,
        align: 'left',
      },
      xAxis: [{
        categories: [],
        type: 'category',
        crosshair: true,
        labels: CHART_STYLES.xAxisLabels,
      }],
      yAxis: [{
        tickmarkPlacement: 'on',
        title: {
          text: '',
          style: { fontSize: '1.25rem' },
        },
        labels: {
          format: '{value:.0f}M',
          style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.caption2, fontFamily: 'Inter, sans-serif' },
        },
        stackLabels: {
          style: { fontSize: '1rem' },
          enabled: true,
          formatter(this: Highcharts.StackItemObject): string {
            if (this.total !== 0) {
              return Highcharts.numberFormat(this.total, 2);
            }
          },
        },
      }, getChartSecondaryAxis()],
      tooltip: CHART_STYLES.tooltip,
      legend: CHART_STYLES.legend(),
      plotOptions: {
        column: {
          width: 100,
          stacking: 'normal',
          dataLabels: {
            pointFormat: '{point.y:.2f}',
            enabled: true,
            inside: true,
            color: 'white',
            rotation: 270,
            style: {
              fontSize: '0.75rem',
              textShadow: false,
              textOutline: 'none',
            },
          },
        },
      },
      colors: CHART_COLORS.columnCharts,
      lang: CHART_STYLES.lang,
      series: [
        getChartSeriesObj('Gain/(loss)', 'column', '', 'M'),
        getChartSeriesObj('Investment', 'column', '', 'M'),
        getChartSeriesObj('Holding', 'spline', '', '%', 1, !isMoicCardChart),
        getChartSeriesObj('MOIC', 'spline', 'x', '', 1, isMoicCardChart),
      ],
    };
  }
}

export enum CardChartTitle {
  investment = 'Investment & Gain/(loss)',
  holding = 'Holding',
  moic = 'MOIC',
}
